import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AgentAvatar from '../common/AgentAvatar';
import { useRegisterAction } from '../../hooks/useRegisterAction';
import styles from './Hero.module.css';

const MSG = 'Find me 25 VPs of Ops at Southeast logistics companies.';
const TOTAL = 25;
const VERIFIED = 18;
const FILTERED = 7;

// Every element stays mounted the whole time (revealed via opacity) so the
// panel never changes height mid-sequence.
const START = {
  youOn: false,
  typed: 0,
  caret: false,
  hunterOn: false,
  companiesOn: false,
  companies: 0,
  verifierOn: false,
  checked: 0,
  resultsOn: false,
  verified: 0,
  filtered: 0,
};

const FINAL = {
  youOn: true,
  typed: MSG.length,
  caret: false,
  hunterOn: true,
  companiesOn: true,
  companies: TOTAL,
  verifierOn: true,
  checked: TOTAL,
  resultsOn: true,
  verified: VERIFIED,
  filtered: FILTERED,
};

// Pad counters with a non-breaking space so ticking digits don't shift the
// text around them (mono font).
const NBSP = String.fromCharCode(160);
const pad = (n) => String(n).padStart(2, NBSP);

export default function Hero() {
  const navigate = useNavigate();
  const register = useRegisterAction();
  const [s, setS] = useState(START);

  useEffect(() => {
    // Reduced motion: show the finished hunt, skip the theater.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setS(FINAL);
      return undefined;
    }

    let alive = true;
    const timers = [];
    const patch = (p) => { if (alive) setS((prev) => ({ ...prev, ...p })); };
    const wait = (ms) => new Promise((res) => timers.push(setTimeout(res, ms)));
    const tick = (key, to, stepMs) =>
      new Promise((res) => {
        let n = 0;
        const iv = setInterval(() => {
          if (!alive) { clearInterval(iv); return; }
          n += 1;
          setS((prev) => ({ ...prev, [key]: n }));
          if (n >= to) { clearInterval(iv); res(); }
        }, stepMs);
        timers.push(iv);
      });

    (async () => {
      while (alive) {
        patch(START);
        await wait(700);
        patch({ youOn: true, caret: true });
        await wait(350);
        await tick('typed', MSG.length, 26);
        await wait(400);
        patch({ caret: false });
        await wait(350);
        patch({ hunterOn: true });
        await wait(900);
        patch({ companiesOn: true });
        await tick('companies', TOTAL, 34);
        await wait(800);
        patch({ verifierOn: true });
        await wait(600);
        await tick('checked', TOTAL, 65);
        await wait(500);
        patch({ resultsOn: true });
        await wait(250);
        await Promise.all([
          tick('verified', VERIFIED, 45),
          tick('filtered', FILTERED, 110),
        ]);
        await wait(6500);
      }
    })();

    return () => {
      alive = false;
      timers.forEach((t) => { clearTimeout(t); clearInterval(t); });
    };
  }, []);

  return (
    <section className={styles.hero}>
      <div className={styles.copy}>
        <span className={styles.eyebrow}>
          <AgentAvatar agent="hunter" size={20} /> Hunter
          <AgentAvatar agent="verifier" size={20} /> Verifier
          <AgentAvatar agent="analyst" size={20} /> Analyst
          <AgentAvatar agent="composer" size={20} /> Composer
          <span className={styles.eyebrowSep}>·</span> one chat
        </span>

        <h1 className={styles.headline}>Human-quality prospecting at AI speed.</h1>

        <p className={styles.sub}>
          We built RepBuddy because sales reps shouldn't have to choose between list
          quality and speed. Instead of bouncing accounts against a stale database, our AI
          mimics a live, human workflow, verifying current roles and testing emails.
        </p>

        <div className={styles.ctaRow}>
          <button
            className={styles.primary}
            onClick={register.go}
          >
            {register.label}
          </button>
          <button className={styles.secondary} onClick={() => navigate('/pricing')}>
            See pricing
          </button>
        </div>

        <p className={styles.trial}>7 days · 30 verified leads · no credit card</p>
      </div>

      {/* Agents doing work - not a dashboard */}
      <div className={styles.demo} aria-hidden="true">
        <div className={styles.demoBar}>
          <span className={styles.demoDot} />
          <span className={styles.demoDot} />
          <span className={styles.demoDot} />
          <span className={styles.demoTitle}>live hunt</span>
        </div>

        <div className={styles.chat}>
          <div className={`${styles.you} ${styles.reveal} ${s.youOn ? styles.on : ''}`}>
            <span>{MSG.slice(0, s.typed)}</span>
            {s.caret && <span className={styles.caret} />}
            <span className={styles.ghost}>{MSG.slice(s.typed)}</span>
          </div>

          <div className={`${styles.agent} ${styles.reveal} ${s.hunterOn ? styles.on : ''}`}>
            <span className={`${styles.tag} ${styles.hunter}`}>
              <AgentAvatar agent="hunter" size={20} /> Hunter
              <span className={`${styles.pulse} ${s.verifierOn ? styles.still : ''}`} />
            </span>
            <p className={styles.agentText}>
              Searching the contact database…{' '}
              <span className={`${styles.fade} ${s.companiesOn ? styles.fadeOn : ''}`}>
                <strong>{pad(s.companies)} companies</strong> match your ICP.
              </span>
            </p>
          </div>

          <div className={`${styles.agent} ${styles.reveal} ${s.verifierOn ? styles.on : ''}`}>
            <span className={`${styles.tag} ${styles.verifier}`}>
              <AgentAvatar agent="verifier" size={20} /> Verifier
              <span className={`${styles.pulse} ${s.resultsOn ? styles.still : ''}`} />
            </span>
            <p className={styles.agentText}>
              Running email deliverability checks + employment verification…{' '}
              <span className={styles.checkCount}>{pad(s.checked)}/{TOTAL}</span>
            </p>
            <div className={styles.checkbar}>
              <div
                className={styles.checkfill}
                style={{ width: `${(s.checked / TOTAL) * 100}%` }}
              />
            </div>
          </div>

          <div className={`${styles.result} ${styles.reveal} ${s.resultsOn ? styles.on : ''}`}>
            <div className={styles.resultRow}>
              <span className={styles.ok}>{pad(s.verified)} verified leads</span>
              <span className={styles.muted}>ready to email</span>
            </div>
            <div className={styles.resultRow}>
              <span className={styles.filtered}>{pad(s.filtered)} filtered out</span>
              <span className={styles.muted}>bounced, moved on, or off-ICP</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
